import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import { useToast } from '../contexts/ToastContext';
import { FiAward, FiCalendar, FiUpload, FiFileText } from 'react-icons/fi';
import { FiUser, FiTag } from 'react-icons/fi';
import apiClient, { endpoints } from '../api/client';
import { FileUpload } from './FileUpload';

const EditCertificateModal = ({ data, isOpen, onClose, onCertificateUpdated }) => {
    const [formData, setFormData] = useState(data ?? {
        id: '',
        type: '',
        expiry_date: '',
        user_id: '',
        file: null,
        file_url: null,
        tags: [],
        notes: ''
    });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [availableTags, setAvailableTags] = useState([]);
    const [errors, setErrors] = useState({});
    const { showSuccess, showError } = useToast();

    // Fetch available tags for the modal
    const fetchAvailableTags = async () => {
        try {
            const params = new URLSearchParams({
                limit: 100, // Get all tags for selection
            });

            const response = await apiClient.get(`/api/tags?${params}`);
            if (response.data.success) {
                setAvailableTags(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching tags:', err);
            showError('Failed to load available tags');
        }
    };

    useEffect(() => {
        fetchAvailableTags();
    }, []);

    useEffect(() => {
        console.log("form", data);
    }, [data])

    const certificateTypes = [
        'AHA',
        'HSI',
        'Red Cross',
        'Custom'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleTagToggle = (tag) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.includes(tag)
                ? prev.tags.filter(t => t !== tag)
                : [...prev.tags, tag]
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData(prev => ({ ...prev, file, file_url: null }));
        
        if (errors.file) {
            setErrors(prev => ({ ...prev, file: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.type.trim()) {
            newErrors.certificate_type = 'Certificate type is required';
        }

        if (!formData.expiry_date) {
            newErrors.expiry_date = 'Expiry date is required';
        } else {
            const expiryDate = new Date(formData.expiry_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (expiryDate < today) {
                newErrors.expiry_date = 'Expiry date cannot be in the past';
            }
        }

        if (!formData.file && !formData.file_url) {
            newErrors.file = 'Please upload a certificate file';
        }
        
        if (formData.file) {
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(formData.file.type)) {
                newErrors.file = 'Please upload a PDF or image file (JPG, PNG)';
            }

            const maxSize = 10 * 1024 * 1024; // 10MB
            if (formData.file.size > maxSize) {
                newErrors.file = 'File size must be less than 10MB';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('type', formData.type); // Backend expects 'type', not 'certificate_type'
            formDataToSend.append('expiry_date', formData.expiry_date);
            formDataToSend.append('tag', formData.tags);
            formDataToSend.append('notes', formData.notes || '');

            if (formData.file) {
                formDataToSend.append('certificate_file', formData.file); // Backend expects 'certificate_file', not 'file'
            }

            console.log("form", formData)

            const response = await apiClient.put(`/api/certificates/${formData.id}`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                const updatedCertificate = response.data.data;

                // Step 2: Add tags if any were selected
                if (formData.tags.length > 0) {
                    try {
                        console.log('Adding tags to certificate:', formData.tags);
                        const newTags = formData.tags.filter(tag => !updatedCertificate.tags.some(t => t.name === tag.name));

                        // Add tags to the certificate via the certificate-tags endpoint
                        for (const tag of newTags) {
                            await apiClient.post(endpoints.certificates.tags(updatedCertificate.id), { tag_name: tag.name });
                        }

                        console.log('Tags added successfully');
                    } catch (tagError) {
                        console.warn('Failed to add some tags:', tagError);
                        // Don't fail the entire operation if tags fail
                        showSuccess('Certificate updated successfully, but some tags could not be added.');
                    }
                }

                showSuccess('Certificate updated successfully');
                onCertificateUpdated(response.data.data);
                handleClose();
            } else {
                showError(response.data.message || 'Failed to update certificate');
            }
        } catch (error) {
            // Comprehensive error logging for debugging
            console.error('Error updating certificate:', error);

            // Log detailed AxiosError information
            if (error.isAxiosError) {
                console.error('Axios Error Details:', {
                    message: error.message,
                    name: error.name,
                    code: error.code,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    headers: error.response?.headers,
                    config: {
                        url: error.config?.url,
                        method: error.config?.method,
                        baseURL: error.config?.baseURL,
                        headers: error.config?.headers,
                        timeout: error.config?.timeout
                    }
                });

                // Log full error stack trace
                console.error('Error Stack Trace:', error.stack);
            }

            // Determine appropriate error message based on status code
            let errorMessage = 'Failed to update certificate';

            if (error.response) {
                const status = error.response.status;
                const serverMessage = error.response.data?.message;

                switch (status) {
                    case 400:
                        errorMessage = serverMessage || 'Invalid certificate data provided. Please check all required fields.';
                        break;
                    case 401:
                        errorMessage = 'Authentication required. Please log in again.';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to update certificates.';
                        break;
                    case 404:
                        errorMessage = 'Certificate update endpoint not found.';
                        break;
                    case 413:
                        errorMessage = 'Certificate file is too large. Please choose a smaller file.';
                        break;
                    case 422:
                        errorMessage = serverMessage || 'Invalid certificate data format.';
                        break;
                    case 500:
                        errorMessage = 'Server error occurred. Please try again later.';
                        break;
                    default:
                        errorMessage = serverMessage || `Request failed with status ${status}`;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection and try again.';
                console.error('Network Error - No response received:', error.request);
            } else {
                errorMessage = 'An unexpected error occurred while updating the certificate.';
                console.error('Request Setup Error:', error.message);
            }

            showError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            id: '',
            type: '',
            expiry_date: '',
            file: null,
            tags: [],
            notes: ''
        });
        setErrors({});
        onClose();
    };

    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Edit Certificate" size="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Certificate Type */}
                <div>
                    <label htmlFor="certificate_type" className="block text-sm font-medium text-gray-700 mb-1">
                        <FiAward className="inline h-4 w-4 mr-1" />
                        Certificate Type *
                    </label>
                    <select
                        id="certificate_type"
                        name="certificate_type"
                        value={formData.type}
                        onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                        className={`block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.certificate_type ? 'border-red-300' : 'border-gray-300'
                            }`}
                    >
                        <option value="">Select certificate type</option>
                        {certificateTypes.map(type => (
                            <option key={type} value={type}>
                                {type}
                            </option>
                        ))}
                    </select>
                    {errors.certificate_type && (
                        <p className="mt-1 text-sm text-red-600">{errors.certificate_type}</p>
                    )}
                </div>

                {/* Expiry Date */}
                <div>
                    <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700 mb-1">
                        <FiCalendar className="inline h-4 w-4 mr-1" />
                        Expiry Date *
                    </label>
                    <input
                        type="date"
                        id="expiry_date"
                        name="expiry_date"
                        value={formData.expiry_date}
                        onChange={handleInputChange}
                        min={getMinDate()}
                        className={`block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.expiry_date ? 'border-red-300' : 'border-gray-300'
                            }`}
                    />
                    {errors.expiry_date && (
                        <p className="mt-1 text-sm text-red-600">{errors.expiry_date}</p>
                    )}
                </div>

                {/* File Upload */}
                <div>
                    <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-1">
                        <FiUpload className="inline h-4 w-4 mr-1" />
                        Certificate File *
                    </label>
                    <FileUpload file={formData.file ?? formData.file_url} onChange={handleFileChange} />
                    {errors.file && (
                        <p className="mt-1 text-sm text-red-600">{errors.file}</p>
                    )}
                </div>

                {/* Notes */}
                <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                        <FiFileText className="inline h-4 w-4 mr-1" />
                        Notes (Optional)
                    </label>
                    <textarea
                        id="notes"
                        name="notes"
                        rows={3}
                        value={formData.notes}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Add any additional notes about this certificate..."
                    />
                </div>

                {/* Tags */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FiTag className="inline h-4 w-4 mr-1" />
                        Tags
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {availableTags.map((tag) => (
                            <button
                                key={tag.name}
                                type="button"
                                onClick={() => handleTagToggle(tag)}
                                className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 btn-animate ${formData.tags.find((item) => tag.name === item.name)
                                    ? 'bg-primary-100 text-primary-800 border-primary-200'
                                    : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                                    } border`}
                            >
                                {tag.name}
                            </button>
                        ))}
                    </div>
                    {formData.tags.length > 0 && (
                        <p className="mt-1 text-sm text-gray-500">
                            Selected: {formData.tags.map((item) => (item.name)).join(', ')}
                        </p>
                    )}
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 btn-animate"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <FiAward className="-ml-1 mr-2 h-4 w-4 inline" />
                                Save Certificate
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default EditCertificateModal;